import React, { useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import ApplyPanel from './ApplyPanel'
import DroppedMembers from './DroppedMembers'
import MergeGroupCard from './MergeGroupCard'
import useDecisions from './useDecisions'
import { combineBest, survivorDraft } from './survivorDraft'

// Duplicate courses.
//
// The one irreversible pass. A rename is undone by another rename; a merge
// re-points layouts, writes version-chain links and deactivates the absorbed
// courses, and there is no inverse — so the button says so, and the dry run is not
// optional.
//
// What a merge does *not* touch is why it is safe for people who have played
// there: games, favourites and play counters all reference layout ids, and a merge
// never deletes or renumbers a layout. Every scorecard still resolves to the exact
// layout it was played on.
//
// Two drafts, not one. The accept/skip decision is per group and tiny; the survivor's picked fields
// are a second, much larger object per group, and they are kept apart so a mis-click on Skip cannot
// throw away five minutes of picking. Same shape as the layouts tab, which keeps its course renames
// in their own key for the same reason. Both are in localStorage, so a reload mid-review loses
// nothing — the server holds no decision state at all.
export default function MergesPass({
  country,
  data,
  namesOutstanding,
  onDryRun,
  onApply,
  applying,
  dryRunSeen,
}) {
  const { decisions, setDecision, clear } = useDecisions('merges', country)
  const survivors = useDecisions('merge-survivors', country)
  // Which groups have had a dry run read. Per group, and deliberately not persisted: "I read the
  // preview" is a fact about this sitting, and a stale one is exactly the confidence that guard
  // exists to withhold.
  const [previewed, setPreviewed] = useState({})

  const groups = useMemo(() => data?.groups || [], [data])

  if (!data) return null

  // Accept is the default: every proposed group is already believed.
  const isAccepted = (group) => decisions[group.groupKey] !== false

  const accepted = groups.filter(isAccepted)

  // Accept is the default, so "merge all" is really "undo every skip". Both are
  // here because a pass over thirty groups that ends in one mis-click should not
  // mean thirty clicks back.
  const mergeAll = () => groups.forEach((g) => setDecision(g.groupKey, undefined))
  const skipAll = () => groups.forEach((g) => setDecision(g.groupKey, false))

  // The draft the editor shows: what has been picked, or the survivor exactly as it stands. An
  // untouched group has no draft at all and merges as it always has.
  const draftFor = (group) => survivors.decisions[group.groupKey] || survivorDraft(group)

  const setDraft = (group, draft) => survivors.setDecision(group.groupKey, draft)

  const combineAll = () => groups.forEach((g) => setDraft(g, combineBest(g)))

  const bodyFor = (group) => ({
    groupKey: group.groupKey,
    survivorId: group.survivor.courseId,
    members: group.members.map((m) => ({
      courseId: m.courseId,
      name: m.name,
      active: m.active,
    })),
    // Only when it was actually edited. A group with no draft sends no survivor block, and the
    // server then writes it exactly as it did before any of this existed.
    ...(survivors.decisions[group.groupKey]
      ? { survivor: survivors.decisions[group.groupKey] }
      : {}),
  })

  const submit = (dryRun) => {
    const payload = accepted.map(bodyFor)
    ;(dryRun ? onDryRun : onApply)(payload, dryRun ? undefined : clear)
  }

  // One group on its own. `silent` keeps this out of the page-level dry-run gate: a preview of one
  // group must not unlock the apply button for the other twenty-nine.
  const submitGroup = (group, dryRun) => {
    const payload = [bodyFor(group)]
    if (dryRun) {
      onDryRun(payload, undefined, { silent: true })
      setPreviewed((current) => ({ ...current, [group.groupKey]: true }))
      return
    }
    onApply(
      payload,
      () => {
        // Only this group's drafts. The rest of the page is still mid-review.
        setDecision(group.groupKey, undefined)
        survivors.setDecision(group.groupKey, undefined)
        setPreviewed((current) => ({ ...current, [group.groupKey]: false }))
      },
      { silent: true }
    )
  }

  return (
    <>
      {namesOutstanding > 0 && (
        <Alert variant="warning">
          Course names for this country still have <strong>{namesOutstanding}</strong>{' '}
          unreviewed proposals. Merge clustering compares names, and hiding removes
          trash before clustering — run those passes first, or this will compare
          names a rename was about to change.
        </Alert>
      )}

      {groups.length === 0 && (
        <Alert variant="light">
          No merge groups proposed. That is not by itself evidence the data is clean —
          read the refused list below.
        </Alert>
      )}

      {groups.length > 0 && (
        <p className="text-muted small">
          Most plausible first. The score is out of 100: the name is worth up to 50, how
          far apart they are up to 35, and layouts they share up to 15. A course nobody
          ever placed on a map has no distance to score — it is still proposed on its
          name, and shows <em>no location</em> rather than a distance.
        </p>
      )}

      {groups.length > 0 && (
        <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
          <Button size="sm" variant="outline-success" onClick={mergeAll}>
            Merge all {groups.length}
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={skipAll}>
            Skip all
          </Button>
          <Button size="sm" variant="outline-primary" onClick={combineAll}>
            Combine data in all {groups.length}
          </Button>
          <span className="text-muted small">
            <strong>{accepted.length}</strong> of {groups.length} in the batch
          </span>
        </div>
      )}

      {/* Every group skipped means there is nothing to submit and the apply panel is
          gone. Say so: an empty page with greyed-out cards otherwise reads as a page
          that has broken rather than one that has been told to do nothing. And the
          skips are in localStorage, so this survives a reload and does not explain
          itself the second time either. */}
      {groups.length > 0 && accepted.length === 0 && (
        <Alert variant="warning">
          All {groups.length} groups are skipped, so there is nothing to apply and the
          apply panel is hidden. Press <strong>Merge</strong> on the groups you want, or{' '}
          <Alert.Link href="#" onClick={(e) => { e.preventDefault(); mergeAll() }}>
            put all {groups.length} back in the batch
          </Alert.Link>
          . Skips are remembered in this browser until you change them.
        </Alert>
      )}

      {groups.map((group) => (
        <MergeGroupCard
          key={group.groupKey}
          group={group}
          accepted={isAccepted(group)}
          // undefined rather than true: accept is the default, so an accepted group
          // is the absence of a decision and the saved draft stays small.
          onChange={(value) => setDecision(group.groupKey, value ? undefined : false)}
          draft={draftFor(group)}
          onDraftChange={(draft) => setDraft(group, draft)}
          onCombine={() => setDraft(group, combineBest(group))}
          onReset={() => survivors.setDecision(group.groupKey, undefined)}
          onPreview={() => submitGroup(group, true)}
          onMerge={() => submitGroup(group, false)}
          previewed={!!previewed[group.groupKey]}
          applying={applying}
        />
      ))}

      <DroppedMembers
        dropped={data.dropped || []}
        oversized={data.oversized || []}
        country={country}
      />

      <ApplyPanel
        decidedCount={accepted.length}
        total={groups.length}
        dryRunSeen={dryRunSeen}
        applying={applying}
        destructive
        applyLabel={`Merge ${accepted.length} groups — this cannot be undone`}
        onDryRun={() => submit(true)}
        onApply={() => submit(false)}
      />
    </>
  )
}
